// GtInterviewsPanel: the interviews for a role, a bench or a candidate.
//
// Staff invite people (from the role's pipeline, the bench's members, or from
// the candidate's own record), try the interview themselves, send links again,
// cancel, and open any interview to watch the recording and read the transcript.
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSourcingFetch } from './GtSourcing';
import API_BASE from '../config';

const STATUS = {
  invited: { label: 'Invited', tone: 'slate' },
  in_progress: { label: 'In progress', tone: 'amber' },
  completed: { label: 'Ready for review', tone: 'teal' },
  scored: { label: 'Scored', tone: 'green' },
  expired: { label: 'Expired', tone: 'grey' },
  abandoned: { label: 'Not finished', tone: 'grey' },
  cancelled: { label: 'Cancelled', tone: 'grey' },
};
const ENDED = {
  completed: 'Nathan finished the interview',
  candidate_ended: 'The candidate ended it',
  time_limit: 'Stopped at the time limit',
  disconnected: 'The call dropped',
  link_expired: 'The link expired unused',
  not_finished: 'Started but not finished before the link expired',
  cancelled: 'Cancelled by GTCO',
  left_window: 'Ended: left the interview window a second time',
};
const FLAG = { left_tab: 'Switched to another tab', left_window: 'Left the window for another window or app', left_fullscreen: 'Left full screen', returned: 'Came back' };
const BAND = { strong: 'Strong', possible: 'Possible', weak: 'Weak' };
const MARK = { clear: 'No integrity concerns', review: 'Check integrity', concern: 'Integrity concern' };
const DECISION = { advance: 'Moving forward', hold: 'On hold', not_a_fit: 'Not a fit' };
const SKIP = {
  already_invited: 'already has an open interview',
  consent_withdrawn: 'has withdrawn consent',
  closed: 'record is closed',
  not_found: 'not found',
};
const MAIL = {
  email_disabled: 'email sending is off on this server',
  unsubscribed: 'unsubscribed',
  preference_off: 'has switched these emails off',
  email_channel_off: 'email is switched off for them',
  consent_withdrawn: 'has withdrawn consent',
  no_email: 'no email address',
  duplicate: 'just sent',
};

function ukDate(v) { if (!v) return ''; const d = new Date(v); return isNaN(d) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
function ukTime(v) { if (!v) return ''; const d = new Date(v); return isNaN(d) ? '' : d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); }
function mins(s) { const n = Math.round((Number(s) || 0) / 60); return n < 1 ? 'under a minute' : n + ' min'; }
function initials(name) { return (name || '?').split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase(); }
function mb(b) { return (Math.round((Number(b) || 0) / 1048576 * 10) / 10) + ' MB'; }
function linkFor(token) { return window.location.origin + '/interview/' + token; }
function offset(at, start) {
  const s = Math.max(0, Math.round((new Date(at).getTime() - new Date(start).getTime()) / 1000));
  return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
}

function Pill({ status }) {
  const s = STATUS[status] || { label: status, tone: 'grey' };
  return <span className={'gtivl-pill gtivl-pill--' + s.tone}>{s.label}</span>;
}

// ── Invite people from a role's pipeline or a bench ──────────────────────
function InviteModal({ kind, ownerId, authFetch, onClose, onDone, flash }) {
  const [people, setPeople] = useState(null);
  const [error, setError] = useState('');
  const [picked, setPicked] = useState({});
  const [q, setQ] = useState('');
  const [email, setEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let live = true;
    authFetch('/api/interviews/eligible?kind=' + kind + '&id=' + ownerId)
      .then((r) => { if (live) setPeople(r.people || []); })
      .catch((e) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [authFetch, kind, ownerId]);

  const blocked = (p) => p.interview_status === 'invited' || p.interview_status === 'in_progress' || p.consent_status === 'withdrawn'
    || p.candidate_status === 'archived' || p.candidate_status === 'deleted';
  const shown = useMemo(() => (people || []).filter((p) => !q || (p.full_name || '').toLowerCase().indexOf(q.toLowerCase()) !== -1), [people, q]);
  const ids = Object.keys(picked).filter((k) => picked[k]).map(Number);
  const free = shown.filter((p) => !blocked(p) && !p.interview_status);

  async function send() {
    setBusy(true);
    try {
      const r = await authFetch('/api/interviews/invite', { method: 'POST', body: JSON.stringify({ kind, id: ownerId, candidate_ids: ids, email }) });
      setResult(r);
      onDone();
      flash(r.created.length === 1 ? 'Invitation created' : r.created.length + ' invitations created');
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  return (
    <div className="gtc-modal-backdrop" onClick={onClose}>
      <div className="gtc-modal gtivl-modal" onClick={(e) => e.stopPropagation()}>
        <div className="gtc-modal-head">{result ? 'Invitations' : 'Invite people to interview'}</div>
        <div className="gtc-modal-body">
          {error && <p className="gtivl-err">{error}</p>}
          {result ? (
            <div className="gtivl-result">
              {result.created.map((c) => (
                <div key={c.id} className="gtivl-res-row">
                  <span className="gtivl-av gtivl-av--sm">{initials(c.name)}</span>
                  <b>{c.name}</b>
                  <span>{c.emailed ? 'Emailed' : 'Not emailed: ' + (MAIL[c.email_reason] || c.email_reason || 'not requested')}</span>
                  <button type="button" className="gtiv-link" onClick={() => { navigator.clipboard.writeText(linkFor(c.token)).then(() => flash('Link copied')).catch(() => {}); }}>Copy link</button>
                </div>
              ))}
              {result.skipped.map((s) => (
                <div key={'s' + s.candidate_id} className="gtivl-res-row gtivl-res-row--skip">
                  <span className="gtivl-av gtivl-av--sm">{initials(s.name)}</span>
                  <b>{s.name || 'Candidate ' + s.candidate_id}</b>
                  <span>Skipped: {SKIP[s.reason] || s.reason}</span>
                </div>
              ))}
            </div>
          ) : people === null ? (
            <p className="gtivl-muted">Loading…</p>
          ) : people.length === 0 ? (
            <p className="gtivl-muted">{kind === 'role' ? 'Nobody is in this role\'s pipeline yet. Add people to the pipeline first.' : 'This bench has no members yet.'}</p>
          ) : (
            <>
              <div className="gtivl-pickbar">
                <input className="gtiv-in" placeholder="Search by name" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search by name" />
                <button type="button" className="gtiv-link" onClick={() => { const n = { ...picked }; free.forEach((p) => { n[p.id] = true; }); setPicked(n); }}>Select everyone not yet invited ({free.length})</button>
              </div>
              <div className="gtivl-picklist">
                {shown.map((p) => {
                  const off = blocked(p);
                  return (
                    <label key={p.id} className={'gtivl-pick' + (picked[p.id] ? ' gtivl-pick--on' : '') + (off ? ' gtivl-pick--off' : '')}>
                      <input type="checkbox" disabled={off} checked={!!picked[p.id]} onChange={(e) => setPicked({ ...picked, [p.id]: e.target.checked })} />
                      <span className="gtivl-av gtivl-av--sm">{initials(p.full_name)}</span>
                      <span className="gtivl-pick-t">
                        <b>{p.full_name}</b>
                        <span>{[p.current_title, p.current_company].filter(Boolean).join(' at ') || p.ref}{p.stage_label ? ' · ' + p.stage_label : ''}{!p.has_email ? ' · no email address' : ''}</span>
                      </span>
                      {p.interview_status ? <Pill status={p.interview_status} /> : p.consent_status === 'withdrawn' ? <span className="gtivl-pill gtivl-pill--grey">Consent withdrawn</span> : null}
                    </label>
                  );
                })}
              </div>
              <label className="gtivl-check">
                <input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} />
                <span>Email each person their link now. It explains the interview is with an AI, is recorded, and that a person reviews it. Untick to copy links and send them yourself.</span>
              </label>
            </>
          )}
        </div>
        <div className="gtc-modal-foot">
          <button type="button" className="gtc-btn-ghost" onClick={onClose}>{result ? 'Close' : 'Cancel'}</button>
          {!result && (
            <button type="button" className="gtc-primary" disabled={busy || !ids.length} onClick={send}>
              {busy ? 'Inviting…' : ids.length ? 'Invite ' + ids.length + (ids.length === 1 ? ' person' : ' people') : 'Invite'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Invite one candidate, choosing the role or bench ─────────────────────
function InviteOneModal({ candidateId, authFetch, onClose, onDone, flash }) {
  const [options, setOptions] = useState(null);
  const [choice, setChoice] = useState('');
  const [email, setEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    authFetch('/api/interviews/options?candidate_id=' + candidateId)
      .then((r) => { if (live) { setOptions(r.options || []); if (r.options && r.options[0]) setChoice(r.options[0].kind + ':' + r.options[0].id); } })
      .catch((e) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [authFetch, candidateId]);
  async function send() {
    const [kind, id] = choice.split(':');
    setBusy(true); setError('');
    try {
      const r = await authFetch('/api/interviews/invite', { method: 'POST', body: JSON.stringify({ kind, id: Number(id), candidate_ids: [candidateId], email }) });
      if (r.created.length) {
        const c = r.created[0];
        flash(c.emailed ? 'Invited and emailed' : 'Invited. Not emailed: ' + (MAIL[c.email_reason] || c.email_reason || 'not requested'));
        onDone(); onClose();
      } else {
        setError('Not invited: ' + (SKIP[r.skipped[0] && r.skipped[0].reason] || 'refused'));
      }
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }
  return (
    <div className="gtc-modal-backdrop" onClick={onClose}>
      <div className="gtc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="gtc-modal-head">Invite to interview</div>
        <div className="gtc-modal-body">
          {error && <p className="gtivl-err">{error}</p>}
          {options === null ? <p className="gtivl-muted">Loading…</p> : options.length === 0 ? (
            <p className="gtivl-muted">Nathan needs an approved plan for one of this candidate's roles or benches. Add them to a role's pipeline, or approve that role's interview plan, first.</p>
          ) : (
            <>
              <div className="gtivl-opts">
                {options.map((o) => {
                  const key = o.kind + ':' + o.id;
                  return (
                    <label key={key} className={'gtivl-pick' + (choice === key ? ' gtivl-pick--on' : '')}>
                      <input type="radio" name="gtivl-opt" checked={choice === key} onChange={() => setChoice(key)} />
                      <span className="gtivl-pick-t"><b>{o.title}</b><span>{o.kind === 'role' ? 'Role' : 'Bench'} · {o.ref}</span></span>
                    </label>
                  );
                })}
              </div>
              <label className="gtivl-check">
                <input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} />
                <span>Email them their link now.</span>
              </label>
            </>
          )}
        </div>
        <div className="gtc-modal-foot">
          <button type="button" className="gtc-btn-ghost" onClick={onClose}>Cancel</button>
          {options && options.length > 0 && <button type="button" className="gtc-primary" disabled={busy || !choice} onClick={send}>{busy ? 'Inviting…' : 'Invite'}</button>}
        </div>
      </div>
    </div>
  );
}

// ── Small pieces ─────────────────────────────────────────────────────────
function ScoreBadge({ pct, band, size }) {
  if (pct == null) return null;
  return <span className={'gtivl-scorebadge gtivl-scorebadge--' + (band || 'none') + (size === 'lg' ? ' gtivl-scorebadge--lg' : '')} title={(BAND[band] || '') + ' match on the interview'}>{pct}%</span>;
}
function Mark({ marker }) {
  if (!marker || marker === 'clear') return null;
  return <span className={'gtivl-mark gtivl-mark--' + marker}>{marker === 'concern' ? 'Integrity concern' : 'Check integrity'}</span>;
}
function VideoChip() {
  return (
    <span className="gtivl-vid" title="Recording available">
      <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true"><path d="M8 5v14l11-7z" fill="currentColor" /></svg>Video
    </span>
  );
}

// ── The assessment: scores, evidence, integrity, and the person's decision ─
function Assessment({ d, busy, onScore, onReview }) {
  const a = d.analysis;
  const [decision, setDecision] = useState(d.review_decision || '');
  const [note, setNote] = useState(d.review_note || '');
  const finished = ['completed', 'scored', 'abandoned'].indexOf(d.status) !== -1;
  if (!finished) {
    return <div className="gtivl-pane"><p className="gtivl-muted">The assessment appears here once the interview has finished.</p></div>;
  }
  if (!a) {
    return (
      <div className="gtivl-pane">
        <div className="gtivl-scoring">
          {d.scoring_status === 'failed' ? (
            <>
              <b>Scoring did not run</b>
              <p className="gtivl-muted">{d.scoring_error || 'Something went wrong.'}</p>
              <button type="button" className="gtc-primary" disabled={!!busy} onClick={onScore}>{busy === 'score' ? 'Scoring…' : 'Score it now'}</button>
            </>
          ) : d.scoring_status === 'empty' ? (
            <><b>Nothing to score</b><p className="gtivl-muted">The candidate did not answer anything.</p></>
          ) : (
            <>
              <span className="gtiv-orb gtivl-spin" aria-hidden="true" />
              <b>Scoring this interview</b>
              <p className="gtivl-muted">A separate AI review reads the transcript against the plan. It usually takes about a minute after the call ends.</p>
              <button type="button" className="gtc-btn-ghost" disabled={!!busy} onClick={onScore}>{busy === 'score' ? 'Scoring…' : 'Score it now'}</button>
            </>
          )}
        </div>
      </div>
    );
  }
  const integ = a.integrity || {};
  return (
    <div className="gtivl-pane">
      <div className="gtivl-sum">
        <div className={'gtivl-ring gtivl-ring--' + (a.band || 'none')}>
          <b>{a.overall_pct == null ? '–' : a.overall_pct + '%'}</b>
          <span>{BAND[a.band] || 'Not scored'}</span>
        </div>
        <div className="gtivl-sum-t">
          <p>{a.summary || 'No summary.'}</p>
          <span className="gtivl-muted">{a.covered} of {a.total} topics scored · weighted towards must-haves{a.scored_at ? ' · scored ' + ukTime(a.scored_at) : ''}</span>
          {a.must_have_gaps && a.must_have_gaps.length > 0 && (
            <span className="gtivl-musts">Must-haves weak or not shown: {a.must_have_gaps.join(', ')}</span>
          )}
        </div>
      </div>

      <div className={'gtivl-integ gtivl-integ--' + (integ.marker || 'clear')}>
        <b>{MARK[integ.marker] || MARK.clear}</b>
        {(integ.notes || []).length > 0 ? <ul>{integ.notes.map((n, i) => <li key={i}>{n}</li>)}</ul> : <span>Nothing unusual in how the interview was taken.</span>}
      </div>

      {((a.strengths || []).length > 0 || (a.gaps || []).length > 0) && (
        <div className="gtivl-sg">
          <div><span className="gtiv-k">Strengths</span><ul>{(a.strengths || []).map((x, i) => <li key={i}>{x}</li>)}</ul></div>
          <div><span className="gtiv-k">Gaps</span><ul>{(a.gaps || []).map((x, i) => <li key={i}>{x}</li>)}</ul></div>
        </div>
      )}

      {Array.isArray(a.panel) && (
        <p className="gtivl-panelnote">
          Averaged from {a.panel.filter((p) => p.ok).length} independent scorers ({a.panel.filter((p) => p.ok).map((p) => p.model).join(', ')}).
          {a.band === 'incomplete' ? ' Not enough of the rubric was covered (' + a.coverage + '% by weight) to give an overall score.' : ''}
          {(a.splits || []).length ? ' They disagree on ' + a.splits.join(', ') + '.' : ''}
        </p>
      )}
      <div className="gtivl-comps">
        {(a.competencies || []).map((c) => (
          <div key={c.key} className="gtivl-comp">
            <div className="gtivl-comp-h">
              <b>{c.name}</b>
              {c.weight === 3 && <span className="gtiv-weight gtiv-weight--3">Must-have</span>}
              <span className={'gtivl-comp-s' + (c.score == null ? ' gtivl-comp-s--none' : c.score >= 3.75 ? ' gtivl-comp-s--hi' : c.score >= 2.75 ? ' gtivl-comp-s--mid' : ' gtivl-comp-s--lo')}>
                {c.score == null ? 'Not scored' : c.score + ' / 5'}
              </span>
            </div>
            {c.score != null && <span className="gtivl-bar"><i style={{ width: (c.score / 5) * 100 + '%' }} /></span>}
            {Array.isArray(c.panel) && c.panel.length > 1 && (
              <div className="gtivl-panel">
                {c.panel.map((p) => <span key={p.model} className="gtivl-pm">{p.model} <b>{p.score == null ? 'not scored' : p.score}</b></span>)}
                {c.spread >= 2 && <span className="gtivl-split">Scorers disagree: check the quotes</span>}
              </div>
            )}
            {c.reasoning && <p className="gtivl-why">{c.reasoning}</p>}
            {(c.evidence || []).map((q, i) => <blockquote key={i} className="gtivl-quote">{q}</blockquote>)}
          </div>
        ))}
      </div>

      <div className="gtivl-review">
        <span className="gtiv-k">Your decision</span>
        {d.review_decision && (
          <p className="gtivl-decided">{DECISION[d.review_decision]}{d.reviewed_by_name ? ' · ' + d.reviewed_by_name : ''}{d.reviewed_at ? ', ' + ukTime(d.reviewed_at) : ''}</p>
        )}
        <div className="gtivl-dec">
          {['advance', 'hold', 'not_a_fit'].map((k) => (
            <button key={k} type="button" className={'gtivl-decbtn gtivl-decbtn--' + k + (decision === k ? ' gtivl-decbtn--on' : '')} onClick={() => setDecision(k)}>
              {k === 'advance' ? 'Move forward' : k === 'hold' ? 'Hold' : 'Not a fit'}
            </button>
          ))}
        </div>
        <textarea className="gtiv-in" rows={2} placeholder={decision === 'not_a_fit' ? 'Reason (required): the candidate can ask a person to review this' : 'Note (optional)'} value={note} onChange={(e) => setNote(e.target.value)} aria-label="Review note" />
        <div className="gtivl-acts">
          <button type="button" className="gtc-primary" disabled={!decision || !!busy || (decision === 'not_a_fit' && !note.trim())} onClick={() => onReview(decision, note)}>
            {busy === 'review' ? 'Saving…' : d.review_decision ? 'Update decision' : 'Record decision'}
          </button>
          <button type="button" className="gtc-btn-ghost" disabled={!!busy} onClick={onScore}>{busy === 'score' ? 'Scoring…' : 'Score again'}</button>
        </div>
        <p className="gtivl-muted">The scores support your judgement; they never decide. Your decision is recorded with your name as the human review.</p>
      </div>
    </div>
  );
}

// ── One interview ─────────────────────────────────────────────────────────
// Clips for employers. Made from a candidate's answer on the transcript, cut
// to their own file on the server, and shown on profile links where the
// candidate's name is shown. Only where they ticked the optional box.
function clipLen(s) { const v = Math.round(Number(s) || 0); return Math.floor(v / 60) + ':' + String(v % 60).padStart(2, '0'); }

function ClipModal({ ivId, turn, authFetch, onClose, onDone }) {
  const [withQ, setWithQ] = useState(true);
  const [plan, setPlan] = useState(null);
  const [title, setTitle] = useState('');
  const touched = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    setPlan(null); setError('');
    authFetch('/api/interviews/' + ivId + '/clips/plan', { method: 'POST', body: JSON.stringify({ turn: turn.turn, include_question: withQ }) })
      .then((p) => { if (live) { setPlan(p); if (!touched.current) setTitle(p.title); } })
      .catch((e) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [authFetch, ivId, turn, withQ]);
  async function make() {
    if (!title.trim()) { setError('Give the clip a title.'); return; }
    setBusy(true); setError('');
    try {
      const r = await authFetch('/api/interviews/' + ivId + '/clips', { method: 'POST', body: JSON.stringify({ turn: turn.turn, include_question: withQ, title: title.trim() }) });
      onDone(r);
    } catch (e) { setError(e.message); setBusy(false); }
  }
  return (
    <div className="gtc-modal-backdrop gtivl-clipback" onClick={(e) => { e.stopPropagation(); if (!busy) onClose(); }}>
      <div className="gtc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="gtc-modal-head">Make a clip for employers</div>
        <div className="gtc-modal-body">
          {error && <p className="gtivl-err">{error}</p>}
          <label className="gtc-field"><span>Title employers see</span>
            <input value={title} maxLength={160} placeholder="e.g. Scaling the ingestion service" onChange={(e) => { touched.current = true; setTitle(e.target.value); }} />
          </label>
          <label className="gtivl-clipq"><input type="checkbox" checked={withQ} onChange={(e) => setWithQ(e.target.checked)} /><span>Start with Nathan's question, so the answer makes sense on its own</span></label>
          {!plan && !error && <p className="gtivl-muted">Working out the timing…</p>}
          {plan && (
            <div className="gtivl-clipplan">
              <span className="gtiv-k">{clipLen(plan.duration_s)} long</span>
              {withQ && plan.question && <p className="gtivl-clipplan-q"><b>Nathan</b> {plan.question}</p>}
              <p className="gtivl-clipplan-a">{plan.answer.length > 420 ? plan.answer.slice(0, 420) + '…' : plan.answer}</p>
            </div>
          )}
        </div>
        <div className="gtc-modal-foot">
          <button type="button" className="gtc-btn-ghost" disabled={busy} onClick={onClose}>Cancel</button>
          <button type="button" className="gtc-primary" disabled={busy || !plan} onClick={make}>{busy ? 'Starting…' : 'Make clip'}</button>
        </div>
      </div>
    </div>
  );
}

function ClipsBox({ data, authFetch, onChange, flash }) {
  if (!data) return null;
  const clips = data.clips || [];
  async function patch(c, body) {
    try { onChange(await authFetch('/api/interview-clips/' + c.id, { method: 'PATCH', body: JSON.stringify(body) })); }
    catch (e) { flash(e.message); }
  }
  async function act(c, what) {
    try {
      onChange(await authFetch('/api/interview-clips/' + c.id + (what === 'retry' ? '/retry' : ''), { method: what === 'retry' ? 'POST' : 'DELETE', body: what === 'retry' ? '{}' : undefined }));
      if (what === 'delete') flash('Clip deleted');
    } catch (e) { flash(e.message); }
  }
  return (
    <div className="gtivl-clips">
      <span className="gtiv-k">Clips for employers{clips.length ? ' · ' + clips.length : ''}</span>
      {!data.clip_consent ? (
        <p className="gtivl-clipnote">They did not tick the box agreeing to clips being shared, so no clips can be made from this interview.</p>
      ) : !data.cutter ? (
        <p className="gtivl-clipnote">Video cutting is not installed on this server yet.</p>
      ) : !clips.length ? (
        <p className="gtivl-clipnote">Press <b>Clip</b> on any of their answers in the transcript. Clips show on profile links where their name is shown.</p>
      ) : null}
      {clips.map((c) => (
        <div key={c.id} className={'gtivl-clip' + (c.share ? '' : ' gtivl-clip--off')}>
          <div className="gtivl-clip-v">
            {c.status === 'ready' && c.url ? (
              <video src={API_BASE + c.url} controls preload="metadata" playsInline><track kind="captions" /></video>
            ) : c.status === 'failed' ? (
              <div className="gtivl-clip-wait gtivl-clip-wait--bad">Could not cut<button type="button" className="gtivl-linkbtn" onClick={() => act(c, 'retry')}>Try again</button></div>
            ) : (
              <div className="gtivl-clip-wait"><i className="gtivl-spin" />Cutting…</div>
            )}
          </div>
          <div className="gtivl-clip-b">
            <input className="gtivl-clip-t" defaultValue={c.title} maxLength={160} aria-label="Clip title"
              onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== c.title) patch(c, { title: v }); }}
              onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
            <div className="gtivl-clip-m">
              <span>{clipLen(c.duration_s)}</span>
              {c.status === 'failed' && c.error && <span className="gtivl-clip-err" title={c.error}>{c.error}</span>}
              <label className="gtivl-clip-sw"><input type="checkbox" checked={c.share} onChange={(e) => patch(c, { share: e.target.checked })} /><span>On profile links</span></label>
              <button type="button" className="gtivl-linkbtn gtivl-danger" onClick={() => act(c, 'delete')}>Delete</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function InterviewView({ id, authFetch, onClose, onChanged, flash }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [rec, setRec] = useState(0);
  const [tab, setTab] = useState('assessment');
  const [clips, setClips] = useState(null);
  const [clipFor, setClipFor] = useState(null);
  const loadClips = useCallback(async () => {
    try { setClips(await authFetch('/api/interviews/' + id + '/clips')); } catch (e) { setClips(null); }
  }, [authFetch, id]);
  useEffect(() => { loadClips(); }, [loadClips]);
  const cutting = !!(clips && clips.clips.some((c) => c.status === 'cutting'));
  useEffect(() => {
    if (!cutting) return undefined;
    const t = setInterval(loadClips, 3000);
    return () => clearInterval(t);
  }, [cutting, loadClips]);
  const load = useCallback(async () => {
    try { setD(await authFetch('/api/interviews/' + id)); } catch (e) { setError(e.message); }
  }, [authFetch, id]);
  useEffect(() => { load(); }, [load]);
  // While a score is on its way, look again every few seconds.
  const waiting = d && !d.analysis && ['completed', 'abandoned'].indexOf(d.status) !== -1 && d.scoring_status !== 'failed' && d.scoring_status !== 'empty';
  useEffect(() => {
    if (!waiting) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [waiting, load]);

  async function act(what, body) {
    setBusy(what);
    try {
      const r = await authFetch('/api/interviews/' + id + '/' + what, { method: 'POST', body: JSON.stringify(body || {}) });
      setD(r);
      onChanged();
      flash(what === 'cancel' ? 'Interview cancelled'
        : what === 'score' ? 'Scored'
          : what === 'review' ? 'Decision recorded'
            : r.emailed ? 'Link sent again, with a fresh 14 days' : 'Link renewed for 14 days. Not emailed: ' + (MAIL[r.email_reason] || r.email_reason || 'unknown'));
    } catch (e) { flash(e.message); } finally { setBusy(''); }
  }

  const turns = (d && d.transcript) || [];
  const start = turns.length ? turns[0].at : null;
  const open = d && (d.status === 'invited' || d.status === 'in_progress');
  const recs = (d && d.recordings.filter((x) => x.url)) || [];
  const r = recs[rec] || recs[0];
  const flags = (d && Array.isArray(d.suspicion_flags) ? d.suspicion_flags : []);
  const strikes = flags.filter((f) => f.kind === 'left_tab' || f.kind === 'left_window').length;
  const a = d && d.analysis;

  // Video synced to the transcript. Each line knows where it starts in its
  // session's recording. Lines saved before 19 Oct carry only the moment the
  // speaker finished, so their start is worked out from how much was said.
  // Jumps land a moment early so nothing is missed.
  const videoRef = useRef(null);
  const txRef = useRef(null);
  const pendingSeek = useRef(null);
  const [nowSec, setNowSec] = useState(0);
  const [playing, setPlaying] = useState(false);
  const cueFor = {};
  recs.forEach((x, i) => {
    const base = x.started_at ? new Date(x.started_at).getTime() : NaN;
    if (isNaN(base)) return;
    let prevEnd = base;
    let prevCut = false;
    turns.filter((t) => (t.session || 1) === x.session).forEach((t) => {
      const end = new Date(t.at).getTime();
      if (isNaN(end)) return;
      let st = t.start_at ? new Date(t.start_at).getTime() : end - (Math.max(1, t.words || 1) / (t.speaker === 'nathan' ? 2.6 : 2.4)) * 1000;
      if (!t.start_at && !prevCut && st < prevEnd) st = prevEnd;
      prevEnd = end;
      prevCut = !!t.interrupted;
      cueFor[t.turn] = { rec: i, sec: Math.max(0, (st - base) / 1000 - 0.8) };
    });
  });
  const curRec = recs.indexOf(r);
  let activeTurn = null;
  turns.forEach((t) => { const c = cueFor[t.turn]; if (c && c.rec === curRec && c.sec <= nowSec + 0.25) activeTurn = t.turn; });
  const clock = (sec) => { const v = Math.max(0, Math.round(sec + 0.8)); return Math.floor(v / 60) + ':' + String(v % 60).padStart(2, '0'); };
  useEffect(() => { setNowSec(0); setPlaying(false); }, [rec]);
  useEffect(() => {
    if (!playing || activeTurn == null || !txRef.current) return;
    const el = txRef.current.querySelector('[data-turn="' + activeTurn + '"]');
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [activeTurn, playing]);
  function seekTo(t) {
    const c = cueFor[t.turn];
    if (!c) return;
    if (c.rec !== curRec) { pendingSeek.current = c.sec; setRec(c.rec); return; }
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = c.sec;
    v.play().catch(() => {});
  }
  function onMeta() {
    const v = videoRef.current;
    if (v && pendingSeek.current != null) { v.currentTime = pendingSeek.current; pendingSeek.current = null; v.play().catch(() => {}); }
  }

  return (
    <div className="gtc-modal-backdrop" onClick={onClose}>
      <div className="gtc-modal gtivl-view" onClick={(e) => e.stopPropagation()}>
        {!d ? (
          <div className="gtc-modal-body">{error ? <p className="gtivl-err">{error}</p> : <p className="gtivl-muted">Loading…</p>}</div>
        ) : (
          <>
            <div className="gtivl-view-h">
              <span className="gtivl-av">{d.is_test ? 'T' : initials(d.full_name)}</span>
              <div className="gtivl-view-t">
                <b>{d.is_test ? 'Test run by ' + (d.invited_by_name || 'staff') : d.full_name}</b>
                <span>{d.role_title || d.bench_name} · {d.ref} · plan v{d.plan_version}</span>
              </div>
              {a && <ScoreBadge pct={a.overall_pct} band={a.band} />}
              {a && <Mark marker={a.integrity && a.integrity.marker} />}
              {d.review_decision ? <span className={'gtivl-decpill gtivl-decpill--' + d.review_decision}>{DECISION[d.review_decision]}</span> : <Pill status={d.status} />}
              <button type="button" className="gtivl-x" onClick={onClose} aria-label="Close">×</button>
            </div>
            <div className="gtivl-facts">
              <span><b>{mins(d.duration_seconds)}</b> talking</span>
              <span><b>{d.current_competency || 0}</b> of <b>{d.total}</b> topics reached</span>
              <span><b>{d.sessions}</b> {d.sessions === 1 ? 'session' : 'sessions'}</span>
              {d.ended_reason && <span>{ENDED[d.ended_reason] || d.ended_reason}</span>}
              {d.consent_at && <span>Agreed {ukTime(d.consent_at)}</span>}
              {strikes > 0 && <span className="gtivl-fact--warn">Left the interview {strikes === 1 ? 'once' : strikes + ' times'}</span>}
              {open && d.expires_at && <span>Link open until {ukDate(d.expires_at)}</span>}
            </div>
            <div className="gtivl-tabs" role="tablist">
              <button type="button" role="tab" aria-selected={tab === 'assessment'} className={'gtivl-tab' + (tab === 'assessment' ? ' gtivl-tab--on' : '')} onClick={() => setTab('assessment')}>Assessment</button>
              <button type="button" role="tab" aria-selected={tab === 'call'} className={'gtivl-tab' + (tab === 'call' ? ' gtivl-tab--on' : '')} onClick={() => setTab('call')}>Recording and transcript</button>
            </div>
            {tab === 'assessment' ? (
              <div className="gtivl-view-a">
                <Assessment key={d.id + ':' + (d.review_decision || '')} d={d} busy={busy} onScore={() => act('score')} onReview={(decision, note) => act('review', { decision, note })} />
              </div>
            ) : (
              <div className="gtivl-view-b">
                <div className="gtivl-media">
                  {r ? (
                    <>
                      <video key={r.url} ref={videoRef} className="gtivl-video" src={API_BASE + r.url} controls preload="metadata" playsInline
                        onLoadedMetadata={onMeta} onTimeUpdate={(e) => setNowSec(e.currentTarget.currentTime)} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}>
                        <track kind="captions" />
                      </video>
                      {recs.length > 1 && (
                        <div className="gtivl-sessions">
                          {recs.map((x, i) => (
                            <button key={x.n} type="button" className={'gtivl-sess' + (i === rec ? ' gtivl-sess--on' : '')} onClick={() => setRec(i)}>Session {x.session}</button>
                          ))}
                        </div>
                      )}
                      <p className="gtivl-muted">{recs.map((x) => 'Session ' + x.session + ': ' + mb(x.bytes)).join(' · ')}</p>
                    </>
                  ) : (
                    <div className="gtivl-novideo">{d.status === 'invited' ? 'Not started yet.' : 'No recording. The candidate\'s browser may not support recording, or it is still uploading.'}</div>
                  )}
                  <ClipsBox data={clips} authFetch={authFetch} flash={flash} onChange={(x) => setClips((old) => ({ ...(old || {}), ...x }))} />
                  {clipFor && (
                    <ClipModal ivId={id} turn={clipFor} authFetch={authFetch} onClose={() => setClipFor(null)}
                      onDone={(x) => { setClips((old) => ({ ...(old || {}), ...x })); setClipFor(null); flash('Cutting the clip. It appears here in a moment.'); }} />
                  )}
                  {flags.length > 0 && (
                    <div className="gtivl-flags">
                      <span className="gtiv-k">Window activity</span>
                      {flags.map((f, i) => (
                        <div key={i} className="gtivl-flagrow">
                          <b>{FLAG[f.kind] || f.kind}</b>
                          <span>{ukTime(f.at)}{f.seconds ? ' · away ' + f.seconds + 's' : ''}{f.session > 1 ? ' · session ' + f.session : ''}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="gtivl-acts">
                    {open && d.resume_token && <button type="button" className="gtc-btn-ghost" onClick={() => navigator.clipboard.writeText(linkFor(d.resume_token)).then(() => flash('Link copied')).catch(() => {})}>Copy link</button>}
                    {!d.is_test && (open || d.status === 'expired' || d.status === 'abandoned') && <button type="button" className="gtc-btn-ghost" disabled={!!busy} onClick={() => act('resend')}>{busy === 'resend' ? 'Sending…' : 'Send link again'}</button>}
                    {open && <button type="button" className="gtc-btn-ghost gtivl-danger" disabled={!!busy} onClick={() => act('cancel')}>{busy === 'cancel' ? 'Cancelling…' : 'Cancel interview'}</button>}
                  </div>
                </div>
                <div className="gtivl-tx" ref={txRef}>
                  <span className="gtiv-k">Transcript{r && Object.keys(cueFor).length > 0 ? <em className="gtivl-txhint"> · click a line to play from there</em> : null}</span>
                  {turns.length === 0 ? <p className="gtivl-muted">Nothing said yet.</p> : turns.map((t) => (
                    <div key={t.turn} data-turn={t.turn}
                      className={'gtivl-turn gtivl-turn--' + t.speaker + (cueFor[t.turn] ? ' gtivl-turn--seek' : '') + (activeTurn === t.turn ? ' gtivl-turn--now' : '')}
                      role={cueFor[t.turn] ? 'button' : undefined} tabIndex={cueFor[t.turn] ? 0 : undefined} title={cueFor[t.turn] ? 'Play from here' : undefined}
                      onClick={cueFor[t.turn] ? () => seekTo(t) : undefined}
                      onKeyDown={cueFor[t.turn] ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); seekTo(t); } } : undefined}>
                      <div className="gtivl-turn-h">
                        <b>{t.speaker === 'nathan' ? 'Nathan' : d.is_test ? 'Tester' : (d.full_name || 'Candidate').split(' ')[0]}</b>
                        <time>{cueFor[t.turn] ? clock(cueFor[t.turn].sec) : (start ? offset(t.at, start) : '')}</time>
                        {t.speaker === 'candidate' && t.gap_ms != null && t.words > 60 && t.gap_ms < 4000 && <span className="gtivl-flag" title="A long answer that started very quickly. Worth a listen, not a verdict.">quick long answer</span>}
                        {t.session > 1 && <span className="gtivl-sesstag">session {t.session}</span>}
                        {clips && clips.clips.some((c) => t.turn >= c.turn_from && t.turn <= c.turn_to) && <span className="gtivl-cliptag">in a clip</span>}
                        {t.speaker === 'candidate' && clips && clips.clip_consent && clips.cutter && cueFor[t.turn] && (
                          <button type="button" className="gtivl-clipbtn" title="Make a clip of this answer for employers"
                            onClick={(e) => { e.stopPropagation(); setClipFor(t); }} onKeyDown={(e) => e.stopPropagation()}>Clip</button>
                        )}
                      </div>
                      <p>{t.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const FILTERS = [['review', 'To review'], ['open', 'Invited and in progress'], ['reviewed', 'Reviewed'], ['tests', 'Test runs'], ['all', 'All']];

export default function GtInterviewsPanel({ kind, ownerId, getToken, flash: flashProp }) {
  const authFetch = useSourcingFetch(getToken);
  const [toast, setToast] = useState(null);
  const flash = useCallback((m) => {
    if (flashProp) { flashProp(m); return; }
    setToast(m); setTimeout(() => setToast(null), 2800);
  }, [flashProp]);
  const isAll = kind === 'all';
  const [filter, setFilter] = useState('review');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);
  const [viewId, setViewId] = useState(null);
  const [testing, setTesting] = useState(false);
  const [showTests, setShowTests] = useState(false);
  const [status, setStatus] = useState(null);

  const load = useCallback(async () => {
    const q = isAll ? '/api/interviews?kind=all&filter=' + filter : '/api/interviews?kind=' + kind + '&id=' + ownerId;
    try { setData(await authFetch(q)); setError(''); } catch (e) { setError(e.message); }
  }, [authFetch, kind, ownerId, isAll, filter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    let live = true;
    authFetch('/api/interviews/status').then((x) => { if (live) setStatus(x); }).catch(() => {});
    return () => { live = false; };
  }, [authFetch]);
  // Finished interviews are scored in the background: refresh while any wait.
  const pending = !!(data && (data.interviews || []).some((i) => i.status === 'completed' && !i.scoring_status));
  useEffect(() => {
    if (!pending) return undefined;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [pending, load]);

  async function tryIt() {
    const w = window.open('', '_blank');
    setTesting(true);
    try {
      const r = await authFetch('/api/interviews/test', { method: 'POST', body: JSON.stringify({ kind, id: ownerId }) });
      const url = linkFor(r.token);
      if (w) w.location.href = url; else window.location.href = url;
      flash(r.plan_status === 'approved' ? 'Test interview opened in a new tab' : 'Test interview opened on the draft plan (v' + r.plan_version + ')');
      load();
    } catch (e) { if (w) w.close(); flash(e.message); } finally { setTesting(false); }
  }

  // Applicants to this role who have not had an interview yet.
  const [applicants, setApplicants] = useState(0);
  const [invitingAll, setInvitingAll] = useState(false);
  const [allMsg, setAllMsg] = useState('');
  useEffect(() => {
    if (kind !== 'role' || !ownerId || ownerId === 'all') return;
    authFetch('/api/interviews/applicants?role_id=' + ownerId).then((r) => setApplicants((r && r.pending) || 0)).catch(() => {});
  }, [kind, ownerId, authFetch, data]);
  async function inviteAll() {
    setInvitingAll(true); setAllMsg('');
    try {
      const r = await authFetch('/api/interviews/invite-applicants', { method: 'POST', body: JSON.stringify({ role_id: Number(ownerId) }) });
      const made = (r.created || []).length;
      const notMailed = (r.created || []).filter((x) => !x.emailed).length;
      setAllMsg(made + (made === 1 ? ' applicant' : ' applicants') + ' invited and emailed their link.' + (notMailed ? ' ' + notMailed + ' could not be emailed; send their link from the list below.' : '') + ((r.skipped || []).length ? ' ' + r.skipped.length + ' skipped (already invited or consent withdrawn).' : ''));
      setApplicants(0);
      load();
    } catch (e) { setAllMsg(e.message || 'Could not invite them.'); }
    setInvitingAll(false);
  }

  if (error && !data) return <div className="gtiv-card gtiv-err">Could not load interviews: {error} <button type="button" className="gtiv-link" onClick={load}>Try again</button></div>;
  if (!data) return <div className="gtiv-card gtiv-loading">Loading interviews…</div>;

  const all = data.interviews || [];
  const tests = all.filter((i) => i.is_test);
  const real = all.filter((i) => !i.is_test);
  const rows = isAll || showTests ? all : real;
  const count = (st) => real.filter((i) => st.indexOf(i.status) !== -1).length;
  const canInvite = kind === 'candidate' || !!data.approved_plan;

  return (
    <div className={'gtivl' + (isAll ? ' gtivl--all' : '')}>
      {isAll ? (
        <div className="gtivl-filters" role="tablist">
          {FILTERS.map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={filter === k} className={'gtivl-filter' + (filter === k ? ' gtivl-filter--on' : '')} onClick={() => setFilter(k)}>{label}</button>
          ))}
        </div>
      ) : (
        <section className="gtiv-card gtivl-head">
          <div className="gtiv-head-l">
            <span className="gtiv-orb" aria-hidden="true" />
            <div>
              <b>Interviews with Nathan</b>
              <span>
                {real.length === 0 ? (kind === 'candidate' ? 'No interviews yet.' : 'Nobody has been invited yet.') : [
                  count(['invited']) && count(['invited']) + ' invited',
                  count(['in_progress']) && count(['in_progress']) + ' in progress',
                  count(['completed', 'scored']) && count(['completed', 'scored']) + ' finished',
                  count(['expired', 'abandoned', 'cancelled']) && count(['expired', 'abandoned', 'cancelled']) + ' closed',
                ].filter(Boolean).join(' · ')}
              </span>
            </div>
          </div>
          <div className="gtiv-acts">
            {kind !== 'candidate' && <button type="button" className="gtc-btn-ghost" disabled={testing} onClick={tryIt}>{testing ? 'Opening…' : 'Try it yourself'}</button>}
            <button type="button" className="gtc-primary" disabled={!canInvite} title={canInvite ? '' : 'Approve the plan first'} onClick={() => setModal(kind === 'candidate' ? 'one' : 'many')}>
              {kind === 'candidate' ? 'Invite to interview' : 'Invite people'}
            </button>
            {kind === 'role' && applicants > 0 && canInvite && (
              <button type="button" className="gtc-btn-ghost" disabled={invitingAll} onClick={inviteAll} title="Invite everyone who applied and has not had an interview for this role">
                {invitingAll ? 'Inviting…' : 'Invite all applicants (' + applicants + ')'}
              </button>
            )}
          </div>
          {allMsg && <p className="gtiv-note">{allMsg}</p>}
          {kind !== 'candidate' && !data.approved_plan && (
            <p className="gtiv-note gtiv-note--amber">Approve the plan before inviting anyone. You can try it yourself on the draft now.</p>
          )}
          {status && !status.live && (
            <p className="gtiv-note gtiv-note--amber">Live calls are off on this server. Missing settings: {status.missing.join(', ')}.</p>
          )}
        </section>
      )}

      {isAll && rows.length === 0 && (
        <div className="gtiv-card gtivl-empty">
          <span className="gtiv-orb gtiv-orb--lg" aria-hidden="true" />
          <b>{filter === 'review' ? 'Nothing waiting for review' : 'No interviews here'}</b>
          <span>Invite people from a role's Interview tab. Finished interviews are scored automatically and land here for your decision.</span>
        </div>
      )}

      {rows.length > 0 && (
        <div className="gtivl-list">
          {rows.map((i) => (
            <button key={i.id} type="button" className="gtiv-card gtivl-row" onClick={() => setViewId(i.id)}>
              <span className="gtivl-av">{i.is_test ? 'T' : initials(i.full_name)}</span>
              <span className="gtivl-row-t">
                <b>
                  {i.is_test ? 'Test run by ' + (i.invited_by_name || 'staff') : kind === 'candidate' ? (i.role_title || i.bench_name) : i.full_name}
                  {isAll && !i.is_test && <em className="gtivl-for"> · {i.role_title || i.bench_name}</em>}
                  {isAll && i.is_test && <em className="gtivl-for"> · {i.role_title || i.bench_name}</em>}
                </b>
                <span>
                  {i.status === 'invited' ? 'Invited ' + ukDate(i.invited_at) + (i.invited_by_name ? ' by ' + i.invited_by_name : '') + (i.source === 'on_apply' ? ' (part of applying)' : '') + (i.reminders_sent ? ' · ' + i.reminders_sent + (i.reminders_sent === 1 ? ' reminder' : ' reminders') : '')
                    : i.status === 'in_progress' ? 'Started ' + ukTime(i.started_at) + ' · topic ' + (i.current_competency || 1) + ' of ' + i.total
                      : (i.status === 'completed' || i.status === 'scored') ? 'Finished ' + ukTime(i.completed_at) + ' · ' + mins(i.duration_seconds) + ' · ' + i.turns + ' turns'
                        : (ENDED[i.ended_reason] || 'Closed') + (i.turns ? ' · ' + i.turns + ' turns' : '')}
                  {i.review_decision ? ' · ' + DECISION[i.review_decision] + (i.reviewed_by_name ? ' by ' + i.reviewed_by_name : '') : ''}
                </span>
              </span>
              {i.recordings > 0 && <VideoChip />}
              <Mark marker={i.integrity} />
              {i.overall_pct != null ? <ScoreBadge pct={i.overall_pct} band={i.band} />
                : i.status === 'completed' && i.scoring_status !== 'failed' && i.scoring_status !== 'empty' ? <span className="gtivl-pill gtivl-pill--slate">Scoring…</span> : null}
              {i.review_decision ? <span className={'gtivl-decpill gtivl-decpill--' + i.review_decision}>{DECISION[i.review_decision]}</span>
                : i.status === 'scored' ? <span className="gtivl-pill gtivl-pill--teal">To review</span>
                  : i.status === 'completed' ? null : <Pill status={i.status} />}
            </button>
          ))}
        </div>
      )}
      {!isAll && tests.length > 0 && (
        <button type="button" className="gtiv-link gtivl-tests" onClick={() => setShowTests(!showTests)}>
          {showTests ? 'Hide test runs' : 'Show ' + tests.length + (tests.length === 1 ? ' test run' : ' test runs')}
        </button>
      )}

      {modal === 'many' && <InviteModal kind={kind} ownerId={ownerId} authFetch={authFetch} flash={flash} onClose={() => setModal(null)} onDone={load} />}
      {modal === 'one' && <InviteOneModal candidateId={ownerId} authFetch={authFetch} flash={flash} onClose={() => setModal(null)} onDone={load} />}
      {viewId && <InterviewView id={viewId} authFetch={authFetch} flash={flash} onClose={() => setViewId(null)} onChanged={load} />}
      {toast && <div className="gtivl-toast" role="status">{toast}</div>}
    </div>
  );
}
